import { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import RootBoundary from '~/common/errors/RootBoundary.tsx';
import BaseLayout from '~/common/layouts/BaseLayout.tsx';
import EmptyLayout from '~/common/layouts/EmptyLayout.tsx';
import { ADMIN_CONSOLE_ROLES, STORE_CONSOLE_ROLES } from '~/features/admin/data/admin.data.ts';
import AuthGuard from '~/features/auth/components/AuthGuard.tsx';
import RoleGuard from '~/features/auth/components/RoleGuard.tsx';

// ? dynamic import 하는 페이지 컴포넌트들은 네이밍 컨벤션 검사 제외
/* eslint-disable @typescript-eslint/naming-convention */
const AuthSignIn = lazy(() => import('~/pages/auth/sign-in'));
const StoreManagerPage = lazy(() => import('~/pages/store-manager'));
const SalesPage = lazy(() => import('~/pages/sales'));
const OperatorPage = lazy(() => import('~/pages/operator'));
const UserPage = lazy(() => import('~/pages/user'));
const StorePage = lazy(() => import('~/pages/store'));
const RemotePcAgentPage = lazy(() => import('~/pages/remote-pc-agent'));
const NoticePage = lazy(() => import('~/pages/notice'));
const InquiryPage = lazy(() => import('~/pages/inquiry'));
const PurchasePage = lazy(() => import('~/pages/purchase'));
const ProductPage = lazy(() => import('~/pages/product'));
const VendorPage = lazy(() => import('~/pages/vendor'));
const VendorGamePage = lazy(() => import('~/pages/vendor-game'));
const FactorPage = lazy(() => import('~/pages/factor'));
const UserAccessHistoryPage = lazy(() => import('~/pages/user-access-histories'));
const Home = lazy(() => import('~/pages'));
const MyPage = lazy(() => import('~/pages/mypage'));
const SettlementOverviewPage = lazy(() => import('~/pages/dashboard/settlement'));
const StoreSettlementDetailPage = lazy(
  () => import('~/pages/dashboard/settlement/StoreSettlementDetailPage'),
);

// 점주 전용 페이지
const StoreMonitoringPage = lazy(() => import('~/pages/store-console'));
const StoreAnalyticsPage = lazy(() => import('~/pages/store-console/analytics'));
/* eslint-enable @typescript-eslint/naming-convention */

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AuthGuard>
        <BaseLayout />
      </AuthGuard>
    ),
    errorElement: <RootBoundary />,
    children: [
      // 어드민 콘솔 (본사 + 영업)
      {
        element: <RoleGuard allowedRoles={ADMIN_CONSOLE_ROLES} />,
        children: [
          { path: '', element: <Home /> },
          { path: 'store-managers', element: <StoreManagerPage /> },
          { path: 'sales', element: <SalesPage /> },
          { path: 'operators', element: <OperatorPage /> },
          { path: 'users', element: <UserPage /> },
          { path: 'stores', element: <StorePage /> },
          { path: 'stores/:storeId', element: <RemotePcAgentPage /> },
          { path: 'notices', element: <NoticePage /> },
          { path: 'inquiries', element: <InquiryPage /> },
          { path: 'purchases', element: <PurchasePage /> },
          { path: 'products', element: <ProductPage /> },
          { path: 'vendors', element: <VendorPage /> },
          { path: 'vendor-games', element: <VendorGamePage /> },
          { path: 'factors', element: <FactorPage /> },
          { path: 'user-access-histories', element: <UserAccessHistoryPage /> },
          { path: 'dashboard/settlement', element: <SettlementOverviewPage /> },
          { path: 'dashboard/settlement/:storeId', element: <StoreSettlementDetailPage /> },
        ],
      },

      // 점주 콘솔 (점주 + 매니저)
      {
        element: <RoleGuard allowedRoles={STORE_CONSOLE_ROLES} />,
        children: [
          { path: 'store', element: <StoreMonitoringPage /> },
          { path: 'store/analytics', element: <StoreAnalyticsPage /> },
        ],
      },

      // 공통 (역할 무관)
      { path: 'mypage', element: <MyPage /> },
    ],
  },
  {
    path: 'auth',
    element: <EmptyLayout />,
    children: [{ path: 'sign-in', element: <AuthSignIn /> }],
  },
]);

export default router;
